/** @format */

import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import type { Editor } from 'codemirror'
import { Controlled as CodeMirror } from 'react-codemirror2'
import { Copy } from '@king-design/react'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/material.css'
import 'codemirror/mode/shell/shell'
import 'codemirror/mode/python/python'
import 'codemirror/mode/javascript/javascript'
import 'codemirror/mode/yaml/yaml'
import 'codemirror/mode/xml/xml'
import 'codemirror/mode/sql/sql'
import 'codemirror/mode/markdown/markdown'

type CodeLanguage =
  | 'shell'
  | 'bash'
  | 'python'
  | 'javascript'
  | 'typescript'
  | 'json'
  | 'yaml'
  | 'xml'
  | 'sql'
  | 'markdown'
  | string

interface CodeDisplayProps {
  content: string
  language?: CodeLanguage
  width?: string | number
  height?: string | number
  copyable?: boolean
  copyContent?: string
  lineNumbers?: boolean
  readOnly?: boolean
  refreshTrigger?: string | number | boolean
  className?: string
}

const resolveMode = (language: CodeLanguage): string => {
  const lang = language.toLowerCase()
  if (lang === 'bash' || lang === 'shell' || lang === 'sh') return 'shell'
  if (lang === 'python' || lang === 'py') return 'python'
  if (lang === 'json') return 'application/json'
  if (lang === 'typescript' || lang === 'ts') return 'text/typescript'
  if (lang === 'javascript' || lang === 'js') return 'javascript'
  if (lang === 'yaml' || lang === 'yml') return 'yaml'
  if (lang === 'xml' || lang === 'html') return 'xml'
  if (lang === 'sql') return 'sql'
  if (lang === 'markdown' || lang === 'md') return 'markdown'
  return 'shell'
}

const toCssSize = (value: string | number): string => {
  if (typeof value === 'number') return `${value}px`
  return value
}

const CodeDisplay: React.FC<CodeDisplayProps> = ({
  content,
  copyContent,
  language = 'shell',
  width = '100%',
  height = 96,
  copyable = true,
  lineNumbers = false,
  readOnly = true,
  refreshTrigger,
  className
}) => {
  const editorRef = useRef<Editor | null>(null)

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.setValue(content || '')
      editorRef.current.refresh()
    }
  }, [content])

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.refresh()
      setTimeout(() => editorRef.current?.refresh(), 80)
      setTimeout(() => editorRef.current?.refresh(), 220)
    }
  }, [refreshTrigger])

  return (
    <div className={className} style={{ width: toCssSize(width) }}>
      {copyable ? (
        <div className="copy-button">
          <Copy text={copyContent || content}></Copy>
        </div>
      ) : null}
      <CodeMirror
        value={content}
        onBeforeChange={() => null}
        options={{
          mode: resolveMode(language),
          theme: 'material',
          lineNumbers,
          lineWrapping: true,
          readOnly,
          cursorBlinkRate: -1
        }}
        editorDidMount={editor => {
          editorRef.current = editor
          editor.setSize('100%', toCssSize(height))
          editor.refresh()
          requestAnimationFrame(() => editor.refresh())
          setTimeout(() => editor.refresh(), 0)
        }}
      />
    </div>
  )
}

export default styled(CodeDisplay)`
  width: 100%;
  position: relative;

  .copy-button {
    position: absolute;
    top: 8px;
    right: 12px;
    z-index: 20;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }

  &:hover .copy-button {
    opacity: 1;
    pointer-events: auto;
  }

  .CodeMirror {
    width: 100%;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    padding-top: 8px;
  }
`
